// frontend-react/src/services/ruleService.ts
import { message } from 'antd';
import { getApiBaseUrl } from '../config/api';
import { RuleSet, Department, RuleRow, RuleDraft, RuleStorageItem } from '../types/rule';
import type { RuleEditAnalysisResult, RuleOptimizeSuggestion } from '../types/index';

/** ✅ 統一取 token（最小改動：仍以 localStorage 為主） */
const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
};

/**
 * 載入規則集列表
 */
export const loadRuleSets = async (): Promise<RuleSet[]> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/list`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      console.error('載入規則列表失敗:', response.status, response.statusText);
      return [];
    }

    const data = await response.json();
    // 後端若直接回 array（你的情況就是），就直接回傳
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('載入規則列表錯誤:', error);
    return [];
  }
};

/**
 * ✅ 管理用規則集清單（給「已上傳的規則集」區塊）
 * GET /api/custom-rules/manage/list
 * - 無權限：後端 403 -> 前端回傳 null（用來隱藏整塊）
 */
export const loadManageRuleSets = async (): Promise<RuleSet[] | null> => {
  try {
    const token = getAuthToken();
    const url = `${getApiBaseUrl()}/api/custom-rules/manage/list`;

    console.log('[loadManageRuleSets] start', { url, hasToken: !!token });

    const response = await fetch(url, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });

    console.log('[loadManageRuleSets] response', {
      status: response.status,
      ok: response.ok,
    });

    if (response.status === 403) return null;
    if (!response.ok) return [];

    const data = await response.json();
    console.log('[loadManageRuleSets] dataLen', Array.isArray(data) ? data.length : 'not-array');

    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('[loadManageRuleSets] error', e);
    return [];
  }
};
/**
 * ✅ 設為預設規則集（ADMIN）
 * PUT /api/custom-rules/{rule_set_id}/set-default
 */
export const setDefaultRuleSet = async (
  ruleSetId: number
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/set-default`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      return { success: true };
    }

    // 嘗試讀後端錯誤訊息
    const err = await response.json().catch(() => ({}) as any);
    return {
      success: false,
      error: err.detail || err.message || `設定失敗 (HTTP ${response.status})`,
    };
  } catch (error) {
    console.error('設定預設規則失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * ✅ 取消預設規則集（ADMIN）
 * PUT /api/custom-rules/{rule_set_id}/unset-default
 */
export const unsetDefaultRuleSet = async (
  ruleSetId: number
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) return { success: false, error: '未登入' };
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/unset-default`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) return { success: true };
    const err = await response.json().catch(() => ({}) as any);
    return { success: false, error: err.detail || `取消失敗 (HTTP ${response.status})` };
  } catch {
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * ✅ 切換規則集內容鎖定（SUPER_ADMIN）
 * PUT /api/custom-rules/{rule_set_id}/content-lock
 */
export const toggleContentLock = async (
  ruleSetId: number
): Promise<{ success: boolean; content_locked?: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/content-lock`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, content_locked: data.content_locked };
    }

    const err = await response.json().catch(() => ({}) as any);
    return {
      success: false,
      error: err.detail || err.message || `操作失敗 (HTTP ${response.status})`,
    };
  } catch (error) {
    console.error('切換內容鎖定失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 上傳規則 CSV（舊流程保留）
 */
export const uploadRuleCSV = async (
  file: File,
  ruleSetName: string
): Promise<{ success: boolean; data?: any; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (ruleSetName) params.append('rule_set_name', ruleSetName);

    const response = await fetch(
      `${getApiBaseUrl()}/api/custom-rules/upload-csv?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      const error = await response.json().catch(() => ({}));
      return { success: false, error: error.detail || '上傳失敗' };
    }
  } catch (error) {
    console.error('上傳規則失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 上傳規則（新流程：直接送新格式 JSON，讓後端存進 rules_data）
 * POST /api/custom-rules/upload-json
 */
export const uploadRuleSetJson = async (
  ruleSetName: string,
  rules: RuleStorageItem[],
  riskLevelsConfig?: Record<string, string> | null,
  isLiguoLibrary?: boolean,
  isComparison?: boolean,
  sourceFileToken?: string | null
): Promise<{ success: boolean; data?: any; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const body: Record<string, any> = { rule_set_name: ruleSetName, rules };
    if (riskLevelsConfig && Object.keys(riskLevelsConfig).length > 0) {
      body.risk_levels_config = riskLevelsConfig;
    }
    if (isLiguoLibrary) {
      body.is_legaltech_library = true;
    }
    if (isComparison) {
      body.is_comparison = true;
    }
    if (sourceFileToken) {
      body.source_file_token = sourceFileToken;
    }
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/upload-json`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      const error = await response.json().catch(() => ({}));
      return { success: false, error: error.detail || '上傳失敗' };
    }
  } catch (error) {
    console.error('上傳規則(JSON)失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 使用 AI Agent 從文件自動生成審閱規則
 * POST /api/custom-rules/generate-rules
 * - mode: "standard_contract" | "legal_annotation" | "corporate_policy" | "review_target"
 * - file: DOCX 或 PDF 檔案
 */
export const generateRulesFromDocument = async (
  files: File[],
  mode:
    | 'standard_contract'
    | 'legal_annotation'
    | 'corporate_policy'
    | 'review_target'
    | 'auto'
    | 'adhesion_contract'
    | 'adhesion_comparison'
    | 'contract_comparison'
    | 'comparison_auto'
): Promise<{
  success: boolean;
  rules?: RuleDraft[];
  agentTrace?: any;
  detectedMode?: string;
  source_file_token?: string | null;
  error?: string;
}> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('mode', mode);

    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/generate-rules`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && Array.isArray(data.rules)) {
        const rules: RuleDraft[] = data.rules.map((r: any, idx: number) => ({
          key: `rule-${idx}`,
          id: r.id || String(idx + 1),
          name: r.name || '',
          original_text: r.original_text || '',
          ai_text: r.ai_text || '',
          current_version: r.current_version || 'original',
        }));
        return {
          success: true,
          rules,
          agentTrace: data.agent_trace,
          detectedMode: data.detected_mode ?? undefined,
          source_file_token: data.source_file_token ?? null,
        };
      }
      return { success: false, error: data.error || 'AI 生成失敗' };
    } else {
      const error = await response.json().catch(() => ({}));
      return { success: false, error: (error as any).detail || 'AI 生成失敗' };
    }
  } catch (error) {
    console.error('AI 規則生成失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 解析 DOCX 檔案為規則列表（後端解析，不入庫）
 */
export const parseDocxFile = async (
  file: File
): Promise<{ success: boolean; rules?: RuleDraft[]; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/parse-docx`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      // 後端回傳 [{id, name, original_text, ai_text, current_version}, ...]
      // 轉成前端 RuleDraft 格式（加上 key）
      const rules: RuleDraft[] = (data as any[]).map((r, idx) => ({
        key: `rule-${idx}`,
        id: r.id || String(idx + 1),
        name: r.name || '',
        original_text: r.original_text || '',
        ai_text: r.ai_text || '',
        current_version: r.current_version || 'original',
      }));
      return { success: true, rules };
    } else {
      const error = await response.json().catch(() => ({}));
      return { success: false, error: (error as any).detail || 'DOCX 解析失敗' };
    }
  } catch (error) {
    console.error('DOCX 解析失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 刪除規則集
 */
export const deleteRuleSet = async (
  ruleSetId: number
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      return { success: true };
    } else {
      const error = await response.json().catch(() => ({}));
      return { success: false, error: error.detail || '刪除失敗' };
    }
  } catch (error) {
    console.error('刪除規則失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 載入部門列表
 */
export const loadDepartments = async (): Promise<Department[]> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/v1/departments/list`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    }
    return [];
  } catch (error) {
    console.error('載入部門列表失敗:', error);
    return [];
  }
};

/**
 * 載入公司成員列表（供個人授權選擇用）
 */
export const loadCompanyMembers = async (): Promise<
  { id: number; name: string; email: string }[]
> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/v1/user/company-users`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data) ? data : (data.users ?? []);
    }
    return [];
  } catch (error) {
    console.error('載入公司成員失敗:', error);
    return [];
  }
};

/**
 * 載入規則的個人授權列表
 */
export const loadRuleUserPermissions = async (
  ruleId: number
): Promise<{ permission_id: number; user_id: number; user_name: string; user_email: string }[]> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleId}/permissions`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (response.ok) {
      const data = await response.json();
      return data.user_permissions || [];
    }
    return [];
  } catch (error) {
    console.error('載入規則個人權限失敗:', error);
    return [];
  }
};

/**
 * 授予個人規則使用權限（ADMIN 或規則創建者皆可用）
 */
export const grantUserRulePermission = async (
  ruleId: number,
  userId: number
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) return { success: false, error: '未登入' };
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/custom-rules/${ruleId}/permissions/grant?user_ids=${userId}`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.ok) return { success: true };
    const err = await response.json().catch(() => ({}) as any);
    return { success: false, error: err.detail || '授予失敗' };
  } catch {
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 撤銷個人規則使用權限（ADMIN 或規則創建者皆可用）
 */
export const revokeUserRulePermission = async (
  ruleId: number,
  userId: number
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) return { success: false, error: '未登入' };
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/custom-rules/${ruleId}/permissions/revoke?user_ids=${userId}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.ok) return { success: true };
    const err = await response.json().catch(() => ({}) as any);
    return { success: false, error: err.detail || '撤銷失敗' };
  } catch {
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 載入規則的部門權限
 */
export const loadRulePermissions = async (ruleId: number): Promise<number[]> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleId}/permissions`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.ok) {
      const data = await response.json();
      return (data.department_permissions || []).map((p: any) => p.department_id);
    }
    return [];
  } catch (error) {
    console.error('載入規則權限失敗:', error);
    return [];
  }
};

/**
 * 更新規則的部門權限
 */
export const updateRulePermissions = async (
  ruleId: number,
  assignedDepartmentIds: number[]
): Promise<{ success: boolean; error?: string }> => {
  try {
    const token = getAuthToken();
    if (!token) return { success: false, error: '未登入' };

    // 取得當前權限
    const currentDeptIds = await loadRulePermissions(ruleId);

    // 計算需要新增和移除的部門
    const toAdd = assignedDepartmentIds.filter(id => !currentDeptIds.includes(id));
    const toRemove = currentDeptIds.filter(id => !assignedDepartmentIds.includes(id));

    // 新增權限
    if (toAdd.length > 0) {
      await fetch(
        `${getApiBaseUrl()}/api/custom-rules/${ruleId}/permissions/grant?${toAdd.map(id => `department_ids=${id}`).join('&')}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    }

    // 移除權限
    if (toRemove.length > 0) {
      await fetch(
        `${getApiBaseUrl()}/api/custom-rules/${ruleId}/permissions/revoke?${toRemove.map(id => `department_ids=${id}`).join('&')}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    }

    return { success: true };
  } catch (error) {
    console.error('更新部門指派失敗:', error);
    return { success: false, error: '更新失敗' };
  }
};

/**
 * 載入規則集的詳細內容 (用於預覽)
 * ✅ 新舊格式相容，回傳維持舊 RuleRow（中文 key）
 */
export const loadRuleSetDetail = async (ruleSetId: number): Promise<RuleRow[]> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/rules`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) return [];

    const data = await response.json();
    if (!Array.isArray(data)) return [];

    return data.map((rule: any, index: number) => {
      const isNewFormat =
        rule &&
        (rule.id !== undefined || rule.name !== undefined || rule.original_text !== undefined);

      if (isNewFormat) {
        const id = String(rule.id ?? index + 1);
        const name = String(rule.name ?? '');
        const originalText = String(rule.original_text ?? '');
        const aiText = String(rule.ai_text ?? '');
        const currentVersion = String(rule.current_version ?? 'original');

        const content = currentVersion === 'ai' && aiText.trim() ? aiText : originalText;

        return {
          key: `rule-${id || index}`,
          序號: id,
          規則名稱: name,
          規則內容: content,
        };
      }

      return {
        key: `rule-${rule['序號'] || index}`,
        序號: String(rule['序號'] || index + 1),
        規則名稱: rule['規則名稱'] || '',
        規則內容: rule['規則內容'] || '',
      };
    });
  } catch (error) {
    console.error('載入規則詳情失敗:', error);
    return [];
  }
};

/**
 * 載入規則集原始資料（存檔格式，用於編輯後回存）
 * GET /api/custom-rules/{rule_set_id}/rules
 */
export const loadRuleSetRawData = async (ruleSetId: number): Promise<any[]> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/rules`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

/**
 * 更新規則集內容（Admin / Super Admin / 創建者本人，且未鎖定）
 * PUT /api/custom-rules/{rule_set_id}/rules
 */
export const updateRuleSetContent = async (
  ruleSetId: number,
  rules: any[]
): Promise<{ success: boolean; error?: string }> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/rules`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ rules }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      return { success: false, error: err.detail ?? '更新失敗' };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 更新規則集名稱（Admin / Super Admin / 創建者本人）
 * PATCH /api/custom-rules/{rule_set_id}/name
 */
export const updateRuleSetName = async (
  ruleSetId: number,
  name: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/name`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ rule_set_name: name }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      return { success: false, error: err.detail ?? '更新失敗' };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 取得規則集的自訂風險等級定義（若未設定則回傳 null）
 * GET /api/custom-rules/{rule_set_id}
 */
export const loadRuleSetRiskLevels = async (
  ruleSetId: number
): Promise<Record<string, string> | null> => {
  const token = getAuthToken();
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (response.ok) {
      const data = await response.json();
      return data.risk_levels_config ?? null;
    }
  } catch (e) {
    console.error('取得規則集風險等級失敗:', e);
  }
  return null;
};

/**
 * 取得系統預設風險等級定義（供前端顯示參考用）
 * GET /api/custom-rules/risk-levels/defaults
 */
export const getDefaultRiskLevels = async (): Promise<Record<string, string>> => {
  const token = getAuthToken();
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/risk-levels/defaults`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (response.ok) return await response.json();
  } catch (e) {
    console.error('取得預設風險等級失敗:', e);
  }
  return {
    high: '明確違反法規或廣告準則，可能導致消費者投訴、主管機關裁罰；例：虛假性能宣稱、禁用詞彙（最、第一、唯一等無法驗證的絕對化用語）、重大誤導資訊。',
    medium:
      '技術上有違規疑慮，但影響程度中等；例：揭露不完整、未標示適用限制條件、模糊的數據描述、應有但缺少的警語。',
    low: '輕微或建議性質，不影響主要合規；例：格式或措辭建議、最佳實踐改善、可加可不加的說明。',
  };
};

/**
 * 取得公司層級風險等級定義（新規則集建立時的預設顯示值）
 * GET /api/custom-rules/company/risk-levels
 */
export const getCompanyRiskLevels = async (): Promise<Record<string, string>> => {
  const token = getAuthToken();
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/company/risk-levels`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (response.ok) {
      const data = await response.json();
      return { high: data.high, medium: data.medium, low: data.low };
    }
  } catch (e) {
    console.error('取得公司風險等級失敗:', e);
  }
  return {
    high: '明確違反法規或廣告準則，可能導致消費者投訴、主管機關裁罰；例：虛假性能宣稱、禁用詞彙（最、第一、唯一等無法驗證的絕對化用語）、重大誤導資訊。',
    medium:
      '技術上有違規疑慮，但影響程度中等；例：揭露不完整、未標示適用限制條件、模糊的數據描述、應有但缺少的警語。',
    low: '輕微或建議性質，不影響主要合規；例：格式或措辭建議、最佳實踐改善、可加可不加的說明。',
  };
};

/**
 * 更新公司層級風險等級定義（ADMIN only）
 * PUT /api/custom-rules/company/risk-levels
 */
export const updateCompanyRiskLevels = async (
  riskLevelsConfig: Record<string, string> | null
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/company/risk-levels`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ risk_levels_config: riskLevelsConfig }),
    });

    if (response.ok) return { success: true };
    const err = await response.json().catch(() => ({}) as any);
    return {
      success: false,
      error: err.detail || err.message || `更新失敗 (HTTP ${response.status})`,
    };
  } catch (error) {
    console.error('更新公司風險等級定義失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 取得合約比對專屬公司層級風險等級定義
 * GET /api/custom-rules/company/comparison-risk-levels
 */
export const getCompanyComparisonRiskLevels = async (): Promise<Record<string, string>> => {
  const token = getAuthToken();
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/custom-rules/company/comparison-risk-levels`,
      { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
    );
    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.error('取得合約比對風險等級定義失敗:', error);
  }
  return {};
};

/**
 * 更新合約比對專屬公司層級風險等級定義（ADMIN only）
 * PUT /api/custom-rules/company/comparison-risk-levels
 */
export const updateCompanyComparisonRiskLevels = async (
  riskLevelsConfig: Record<string, string> | null
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/custom-rules/company/comparison-risk-levels`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ risk_levels_config: riskLevelsConfig }),
      }
    );

    if (response.ok) return { success: true };
    const err = await response.json().catch(() => ({}) as any);
    return {
      success: false,
      error: err.detail || err.message || `更新失敗 (HTTP ${response.status})`,
    };
  } catch (error) {
    console.error('更新合約比對風險等級定義失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 更新規則集的自訂風險等級定義
 * PUT /api/custom-rules/{rule_set_id}/risk-levels
 * - 傳 null 或 {} → 清除自訂，恢復系統預設
 */
export const updateRiskLevelsConfig = async (
  ruleSetId: number,
  riskLevelsConfig: Record<string, string> | null
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/risk-levels`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ risk_levels_config: riskLevelsConfig }),
    });

    if (response.ok) return { success: true };
    const err = await response.json().catch(() => ({}) as any);
    return {
      success: false,
      error: err.detail || err.message || `更新失敗 (HTTP ${response.status})`,
    };
  } catch (error) {
    console.error('更新風險等級定義失敗:', error);
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 設定或清除個人偏好規則集
 * PUT /api/v1/user/me/preferred-rule
 * - ruleSetId: 規則集 ID，傳 null 代表清除偏好
 */
export const setUserPreferredRule = async (
  ruleSetId: number | null
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) return { success: false, error: '未登入' };
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/user/me/preferred-rule`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rule_set_id: ruleSetId }),
    });
    if (response.ok) return { success: true };
    const err = await response.json().catch(() => ({}) as any);
    return { success: false, error: err.detail || `更新失敗 (HTTP ${response.status})` };
  } catch {
    return { success: false, error: '網路錯誤' };
  }
};

/**
 * 下載規則集為 DOCX
 * GET /api/custom-rules/{rule_set_id}/download-docx
 */
export const downloadRuleSetDocx = async (
  ruleSetId: number,
  ruleSetName: string
): Promise<{ success: boolean; error?: string }> => {
  const token = getAuthToken();
  if (!token) {
    message.error('請先登入');
    return { success: false, error: '未登入' };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/download-docx`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}) as any);
      const msg = err.detail || `下載失敗 (HTTP ${response.status})`;
      message.error(msg);
      return { success: false, error: msg };
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rules_${ruleSetName}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return { success: true };
  } catch (error) {
    console.error('下載規則集 DOCX 失敗:', error);
    message.error('下載失敗，請稍後再試');
    return { success: false, error: '網路錯誤' };
  }
};

// ---------------------------------------------------------------------------
// 規則集編輯分析 & AI 優化
// ---------------------------------------------------------------------------

/**
 * 取得規則集的卡片編輯分析數據（Admin only）
 * GET /api/custom-rules/{ruleSetId}/edit-analysis
 */
export const getRuleEditAnalysis = async (
  ruleSetId: number
): Promise<RuleEditAnalysisResult | null> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/edit-analysis`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!response.ok) {
      console.error('取得編輯分析失敗:', response.status);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('取得編輯分析錯誤:', error);
    return null;
  }
};

/**
 * 刪除單筆卡片編輯紀錄
 * DELETE /api/custom-rules/{ruleSetId}/edit-analysis/{operationId}
 */
export const deleteCardOperation = async (
  ruleSetId: number,
  operationId: number
): Promise<boolean> => {
  try {
    const token = getAuthToken();
    const response = await fetch(
      `${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/edit-analysis/${operationId}`,
      {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      }
    );
    return response.ok;
  } catch (error) {
    console.error('刪除編輯紀錄錯誤:', error);
    return false;
  }
};

/**
 * 清空規則集的所有卡片編輯紀錄
 * DELETE /api/custom-rules/{ruleSetId}/edit-analysis
 */
export const clearAllCardOperations = async (
  ruleSetId: number
): Promise<{ ok: boolean; deleted_count: number }> => {
  const token = getAuthToken();
  const response = await fetch(
    `${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/edit-analysis`,
    {
      method: 'DELETE',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }
  );
  if (!response.ok) {
    throw new Error('清空編輯紀錄失敗');
  }
  return await response.json();
};

/**
 * 產生 AI 規則優化建議（Admin only）
 * POST /api/custom-rules/{ruleSetId}/ai-optimize
 */
export const generateAiRuleSuggestions = async (
  ruleSetId: number,
  userContext?: string
): Promise<RuleOptimizeSuggestion[]> => {
  const token = getAuthToken();
  const response = await fetch(`${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/ai-optimize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ user_context: userContext || '' }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'AI 優化建議產生失敗');
  }
  const data = await response.json();
  return data.suggestions ?? [];
};

/**
 * 套用選中的 AI 規則優化建議（Admin only）
 * POST /api/custom-rules/{ruleSetId}/apply-suggestions
 */
export const applyRuleOptimizeSuggestions = async (
  ruleSetId: number,
  suggestions: RuleOptimizeSuggestion[]
): Promise<{ applied_count: number; message: string }> => {
  const token = getAuthToken();
  const response = await fetch(
    `${getApiBaseUrl()}/api/custom-rules/${ruleSetId}/apply-suggestions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ suggestions }),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || '套用建議失敗');
  }
  return await response.json();
};
